import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Trash2, Plus, Minus, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { API } from "../lib/api";

const CartDrawer = () => {
  const { items, removeItem, setQuantity, totals, open, setOpen } = useCart();
  const navigate = useNavigate();
  const [freeFrom, setFreeFrom] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/shipping-info`).then((r) => setFreeFrom(r.data.free_threshold)).catch(() => {});
  }, [open]);

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[95] h-full w-full sm:w-[440px] bg-[#0a0a0a] border-l border-[var(--tha-border)] transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--tha-border)]">
          <div className="flex items-center gap-3">
            <ShoppingBag size={18} className="text-[var(--tha-red)]" />
            <div className="font-display text-xl tracking-wide">JOUW MANDJE</div>
          </div>
          <button
            data-testid="close-cart-button"
            onClick={() => setOpen(false)}
            className="text-[var(--tha-muted)] hover:text-white"
            aria-label="Sluit winkelmandje"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5" style={{ height: "calc(100% - 200px)" }}>
          {items.length === 0 ? (
            <div
              data-testid="cart-empty"
              className="text-center py-16 text-[var(--tha-muted)]"
            >
              <ShoppingBag size={28} className="mx-auto mb-4 opacity-50" />
              <div className="font-display text-2xl text-white">LEEG.</div>
              <p className="text-sm mt-2">Tijd om er een paar in te tikken.</p>
              {freeFrom > 0 && (
                <p data-testid="cart-empty-free-shipping" className="mt-4 text-[10px] font-mono-x tracking-widest text-[var(--tha-red)]">
                  GRATIS VERZENDING VANAF € {freeFrom.toFixed(2)}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((i) => (
                <div
                  key={i.product_id}
                  data-testid={`cart-item-${i.product_id}`}
                  className="flex gap-4 border border-[var(--tha-border)] p-3"
                >
                  {i.image && (
                    <img
                      src={i.image}
                      alt={i.name}
                      className="w-20 h-20 object-cover bg-black"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-none">{i.name}</div>
                    <div className="text-xs text-[var(--tha-muted)] mt-1">
                      € {i.price.toFixed(2)} per stuk
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[var(--tha-border)]">
                        <button
                          data-testid={`decrease-${i.product_id}`}
                          onClick={() => setQuantity(i.product_id, i.quantity - 1)}
                          className="p-2 hover:bg-[var(--tha-surface)]"
                          aria-label="Min"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm font-mono-x">{i.quantity}</span>
                        <button
                          data-testid={`increase-${i.product_id}`}
                          onClick={() => setQuantity(i.product_id, i.quantity + 1)}
                          className="p-2 hover:bg-[var(--tha-surface)]"
                          aria-label="Plus"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        data-testid={`remove-${i.product_id}`}
                        onClick={() => removeItem(i.product_id)}
                        className="text-[var(--tha-muted)] hover:text-[var(--tha-red)]"
                        aria-label="Verwijder"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-[var(--tha-border)] bg-[#0a0a0a] px-6 py-5">
          {items.length > 0 && freeFrom > 0 && <ShippingProgress subtotal={totals.subtotal} freeFrom={freeFrom} />}
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase tracking-widest text-[var(--tha-muted)] font-mono-x">
              Subtotaal
            </span>
            <span
              data-testid="cart-subtotal"
              className="font-display text-2xl text-white"
            >
              € {totals.subtotal.toFixed(2)}
            </span>
          </div>
          <button
            data-testid="checkout-button"
            disabled={items.length === 0}
            onClick={checkout}
            className="btn-red w-full py-4 font-display tracking-wider text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            AFREKENEN
          </button>
        </div>
      </aside>
    </>
  );
};

const ShippingProgress = ({ subtotal, freeFrom }) => {
  const remaining = Math.max(0, freeFrom - subtotal);
  const pct = Math.min(100, (subtotal / freeFrom) * 100);
  return (
    <div data-testid="cart-shipping-progress" className="mb-4">
      <div className="flex items-center justify-between text-[10px] font-mono-x tracking-widest mb-1.5">
        {remaining > 0 ? (
          <span data-testid="cart-shipping-remaining" className="text-[var(--tha-muted)] inline-flex items-center gap-1.5">
            <Truck size={12} className="text-[var(--tha-red)]" /> NOG <span className="text-[var(--tha-red)]">€ {remaining.toFixed(2)}</span> TOT GRATIS VERZENDING
          </span>
        ) : (
          <span data-testid="cart-shipping-free" className="text-[var(--tha-red)] inline-flex items-center gap-1.5">
            <Truck size={12} /> GRATIS VERZENDING — PROOST!
          </span>
        )}
        <span className="text-[var(--tha-muted)]">€ {freeFrom.toFixed(0)}</span>
      </div>
      <div className="h-1.5 bg-[var(--tha-border)] overflow-hidden">
        <div className="h-full bg-[var(--tha-red)] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default CartDrawer;
